/* eslint-disable max-lines -- verbatim extraction from admin-dashboard-page.tsx; split further when the card is next reworked */
import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Library } from "@/api/admin-api"

import { ChevronDown, Database, Plus, Search, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useAdminCustomerPreviewStore } from "@/stores/admin-customer-preview-store"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { AddLibraryDialog } from "./add-library-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { DeleteLibraryDialog } from "./delete-library-dialog"
import { EditLibraryDialog } from "./edit-library-dialog"
import { LibrariesTable } from "./libraries-table"
import { LibraryDocumentBrowser } from "./library-document-browser"
import {
  editorValuesToCreatePayload,
  editorValuesToUpdatePayload,
  type LibraryEditorValues,
} from "./library-editor/library-editor-model"
import {
  useAdminEmbeddingModelsQuery,
  useAdminLibrariesQuery,
  useAdminLibraryAccessPolicyQuery,
  useAdminLibraryBaseAccessPolicyQuery,
  useClearLibraryAccessPolicyMutation,
  useCreateLibraryMutation,
  useDelegationMatrixQuery,
  useDeleteLibraryMutation,
  useRetryIndexingMutation,
  useUpdateLibraryAccessPolicyMutation,
  useUpdateLibraryBaseAccessPolicyMutation,
  useUpdateLibraryConfigMutation,
  useUpdateLibraryIndexingEnabledMutation,
  useUpdateLibrarySearchEnabledMutation,
} from "../hooks/admin-queries"
import { isPolicyConfigured } from "../utils/access-policy"
import { resolveLibraryAdminPermissions } from "../utils/library-admin-permissions"

export function LibrariesManagementCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [updatingLibraryId, setUpdatingLibraryId] = useState<string | undefined>()
  const [isUpdatingAll, setIsUpdatingAll] = useState(false)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [accessLibraryId, setAccessLibraryId] = useState<string | undefined>()
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)
  const [isAddLibraryDialogOpen, setIsAddLibraryDialogOpen] = useState(false)
  const [isEditLibraryDialogOpen, setIsEditLibraryDialogOpen] = useState(false)
  const [editingLibrary, setEditingLibrary] = useState<Library | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingLibrary, setDeletingLibrary] = useState<Library | null>(null)
  const [isDocumentBrowserOpen, setIsDocumentBrowserOpen] = useState(false)
  const [browsingLibrary, setBrowsingLibrary] = useState<{ id: string; name: string } | null>(null)
  const [librarySearch, setLibrarySearch] = useState("")
  const [libraryStatus, setLibraryStatus] = useState<"all" | "active" | "hidden">("all")

  const { data: appConfig } = useConfigQuery()
  // Customer admins with the `libraries` capability may toggle libraries and
  // edit access; creating, reconfiguring (source credentials) and deleting
  // libraries stays platform-only on the backend, so hide those actions —
  // also while a platform admin previews as a customer admin.
  const isPlatformAdmin = appConfig?.user_policy.is_platform_admin ?? false
  const previewEnabled = useAdminCustomerPreviewStore((s) => s.enabled)
  const { data: delegationData } = useDelegationMatrixQuery(isPlatformAdmin && previewEnabled)
  const { canManagePlatformOnly, canManageIndexing } = resolveLibraryAdminPermissions({
    isPlatformAdmin,
    previewEnabled,
    userCapabilities: appConfig?.user_policy.admin_capabilities,
    previewModes: delegationData?.capabilities,
  })

  const { data: libraries, isLoading } = useAdminLibrariesQuery()
  const { data: embeddingModels } = useAdminEmbeddingModelsQuery(canManagePlatformOnly)
  const updateSearchEnabledMutation = useUpdateLibrarySearchEnabledMutation()
  const updateIndexingEnabledMutation = useUpdateLibraryIndexingEnabledMutation()
  const updateAccessMutation = useUpdateLibraryAccessPolicyMutation()
  const clearAccessMutation = useClearLibraryAccessPolicyMutation()
  const createLibraryMutation = useCreateLibraryMutation()
  const updateLibraryConfigMutation = useUpdateLibraryConfigMutation()
  const deleteLibraryMutation = useDeleteLibraryMutation()
  const retryIndexingMutation = useRetryIndexingMutation()
  const {
    data: baseAccessPolicy,
    isLoading: isLoadingBaseAccessPolicy,
    isError: isBaseAccessPolicyError,
  } = useAdminLibraryBaseAccessPolicyQuery(true)
  const updateBaseAccessMutation = useUpdateLibraryBaseAccessPolicyMutation()
  const {
    data: libraryAccessPolicy,
    isLoading: isLoadingAccessPolicy,
    isError: isAccessPolicyError,
  } = useAdminLibraryAccessPolicyQuery(accessLibraryId, isAccessDialogOpen)

  const handleAddLibrary = async (values: LibraryEditorValues) => {
    try {
      await createLibraryMutation.mutateAsync(editorValuesToCreatePayload(values))
      toast.success(
        t("adminDashboard.libraries.addDialog.success", "Library created successfully"),
        { description: values.display_name }
      )
    } catch (error) {
      toast.error(t("adminDashboard.libraries.addDialog.error", "Failed to create library"), {
        description: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  const handleEditLibrary = async (libraryId: string, values: LibraryEditorValues) => {
    try {
      await updateLibraryConfigMutation.mutateAsync({
        libraryId,
        config: editorValuesToUpdatePayload(values),
      })
      toast.success(
        t("adminDashboard.libraries.editDialog.success", "Library updated successfully"),
        { description: values.display_name }
      )
      setIsEditLibraryDialogOpen(false)
      setEditingLibrary(null)
    } catch (error) {
      toast.error(t("adminDashboard.libraries.editDialog.error", "Failed to update library"), {
        description: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  const handleDeleteLibrary = async (options: {
    deleteIndex: boolean
    deleteTable: boolean
    deleteContainer: boolean
  }) => {
    if (!deletingLibrary) return

    try {
      await deleteLibraryMutation.mutateAsync({
        libraryId: deletingLibrary.id,
        deleteIndex: options.deleteIndex,
        deleteTable: options.deleteTable,
        deleteContainer: options.deleteContainer,
      })
      toast.success(
        t("adminDashboard.libraries.deleteDialog.success", "Library deleted successfully"),
        { description: deletingLibrary.display_name }
      )
      setIsDeleteDialogOpen(false)
      setDeletingLibrary(null)
    } catch (error) {
      toast.error(t("adminDashboard.libraries.deleteDialog.error", "Failed to delete library"), {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleRetryFailed = async (libraryId: string, filenames?: string[]) => {
    try {
      const result = await retryIndexingMutation.mutateAsync({ libraryId, filenames })
      toast.success(t("adminDashboard.indexing.retryTriggered", "Retry indexing triggered"), {
        description: result.message || `Retrying failed files for ${libraryId}`,
      })
    } catch (error) {
      toast.error(t("adminDashboard.indexing.retryError", "Failed to trigger retry"), {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleBrowseDocuments = (libraryId: string, libraryName: string) => {
    setBrowsingLibrary({ id: libraryId, name: libraryName })
    setIsDocumentBrowserOpen(true)
  }

  const handleToggleSearchEnabled = async (libraryId: string, enabled: boolean) => {
    setUpdatingLibraryId(libraryId)
    try {
      await updateSearchEnabledMutation.mutateAsync({ libraryId, enabled })
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingLibraryId(undefined)
    }
  }

  const handleToggleIndexingEnabled = async (libraryId: string, enabled: boolean) => {
    setUpdatingLibraryId(libraryId)
    try {
      await updateIndexingEnabledMutation.mutateAsync({ libraryId, enabled })
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingLibraryId(undefined)
    }
  }

  const handleToggleAllSearch = async (enabled: boolean) => {
    if (!libraries || libraries.length === 0) return

    setIsUpdatingAll(true)
    await queryClient.cancelQueries({ queryKey: ["admin", "navigator", "libraries"] })

    const currentLibraries =
      queryClient.getQueryData<typeof libraries>(["admin", "navigator", "libraries"]) || libraries

    const previousLibraries = currentLibraries
    const librariesToUpdate = currentLibraries.filter((lib) => lib.search_enabled !== enabled)

    // Optimistic update
    queryClient.setQueryData(
      ["admin", "navigator", "libraries"],
      currentLibraries.map((lib) => ({ ...lib, search_enabled: enabled }))
    )

    try {
      // Update each library's search_enabled individually via the dedicated mutation
      const results = await Promise.allSettled(
        librariesToUpdate.map((library) =>
          updateSearchEnabledMutation.mutateAsync({ libraryId: library.id, enabled })
        )
      )
      const hasFailures = results.some((r) => r.status === "rejected")
      if (hasFailures) {
        await queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
      }
    } catch {
      queryClient.setQueryData(["admin", "navigator", "libraries"], previousLibraries)
      await queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  // Determine search toggle state
  const allSearchEnabledState = useMemo(() => {
    if (!libraries || libraries.length === 0) return false
    const enabledCount = libraries.filter((lib) => lib.search_enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === libraries.length) return true
    return undefined // Mixed state
  }, [libraries])

  // Determine indexing toggle state
  const allIndexingEnabledState = useMemo(() => {
    if (!libraries || libraries.length === 0) return false
    const enabledCount = libraries.filter((lib) => lib.indexing_enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === libraries.length) return true
    return undefined // Mixed state
  }, [libraries])

  const handleToggleAllIndexing = async (enabled: boolean) => {
    if (!libraries || libraries.length === 0) return

    setIsUpdatingAll(true)
    await queryClient.cancelQueries({ queryKey: ["admin", "navigator", "libraries"] })

    const currentLibraries =
      queryClient.getQueryData<typeof libraries>(["admin", "navigator", "libraries"]) || libraries

    const previousLibraries = currentLibraries

    // Optimistic update
    queryClient.setQueryData(
      ["admin", "navigator", "libraries"],
      currentLibraries.map((lib) => ({ ...lib, indexing_enabled: enabled }))
    )

    try {
      // Update each library individually (no batch endpoint for indexing_enabled yet)
      await Promise.all(
        currentLibraries
          .filter((lib) => lib.indexing_enabled !== enabled)
          .map((lib) => updateIndexingEnabledMutation.mutateAsync({ libraryId: lib.id, enabled }))
      )
    } catch {
      queryClient.setQueryData(["admin", "navigator", "libraries"], previousLibraries)
      await queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  const selectedLibrary = libraries?.find((lib) => lib.id === accessLibraryId)
  const inheritByDefault = selectedLibrary?.access_overridden !== true

  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])
  const searchableCount = libraries?.filter((library) => library.search_enabled).length ?? 0
  const indexingCount = libraries?.filter((library) => library.indexing_enabled).length ?? 0
  const filteredLibraries = useMemo(() => {
    const normalizedSearch = librarySearch.trim().toLocaleLowerCase()
    return (libraries ?? []).filter((library) => {
      const matchesSearch =
        !normalizedSearch ||
        [library.display_name, library.id, library.description]
          .join(" ")
          .toLocaleLowerCase()
          .includes(normalizedSearch)
      const matchesStatus =
        libraryStatus === "all" ||
        (libraryStatus === "active" && library.search_enabled) ||
        (libraryStatus === "hidden" && !library.search_enabled)
      return matchesSearch && matchesStatus
    })
  }, [libraries, librarySearch, libraryStatus])

  return (
    <>
      <CollapsibleManagementSection
        icon={<Database className="size-5" />}
        id="libraries"
        storageKey="admin-management-libraries-expanded"
        title={t("adminDashboard.libraries.title", "Library Management")}
        headerActions={
          <>
            <details className="group w-full rounded-md border sm:hidden">
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-sm font-medium">
                {t("adminDashboard.libraries.controls", "Library controls")}
                <ChevronDown className="size-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="flex flex-wrap items-center gap-2 border-t p-2">
                <Button
                  className="min-h-11"
                  disabled={isLoading || isUpdatingAll}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => setIsBaseAccessDialogOpen(true)}
                >
                  {baseAccessConfigured ? (
                    <ShieldCheck className="text-success size-4" />
                  ) : (
                    <Shield className="size-4" />
                  )}
                  {t("adminDashboard.libraries.baseAccess", { defaultValue: "Access for all" })}
                </Button>
                <label className="flex min-h-11 items-center gap-2 rounded-md border px-3">
                  <span className="text-xs font-medium">
                    {t("adminDashboard.libraries.enableAllSearch", "Search")}
                  </span>
                  <Switch
                    aria-label={t("adminDashboard.libraries.enableAllSearch", "Search")}
                    checked={allSearchEnabledState === true}
                    disabled={isLoading || isUpdatingAll || !libraries || libraries.length === 0}
                    onCheckedChange={handleToggleAllSearch}
                  />
                </label>
                <label className="flex min-h-11 items-center gap-2 rounded-md border px-3">
                  <span className="text-xs font-medium">
                    {t("adminDashboard.libraries.enableAllIndexing", "Indexing")}
                  </span>
                  <Switch
                    aria-label={t("adminDashboard.libraries.enableAllIndexing", "Indexing")}
                    checked={allIndexingEnabledState === true}
                    disabled={isLoading || isUpdatingAll || !libraries || libraries.length === 0}
                    onCheckedChange={handleToggleAllIndexing}
                  />
                </label>
                <DelegationModeControl capability="libraries" />
              </div>
            </details>
            <div className="hidden min-w-0 flex-wrap items-center gap-2 sm:flex">
              {canManagePlatformOnly ? (
                <Button
                  className="min-h-11 items-center gap-2 px-3"
                  disabled={isLoading || isUpdatingAll}
                  size="sm"
                  type="button"
                  onClick={() => setIsAddLibraryDialogOpen(true)}
                >
                  <Plus className="size-4" />
                  {t("adminDashboard.libraries.addLibrary", "Add Library")}
                </Button>
              ) : null}
              <Button
                className="min-h-11 items-center gap-2 px-3"
                disabled={isLoading || isUpdatingAll}
                size="sm"
                type="button"
                variant="outline"
                onClick={() => setIsBaseAccessDialogOpen(true)}
              >
                {baseAccessConfigured ? (
                  <ShieldCheck className="text-success size-4" />
                ) : (
                  <Shield className="size-4" />
                )}
                {t("adminDashboard.libraries.baseAccess", { defaultValue: "Access for all" })}
              </Button>
              <div className="bg-muted/40 flex min-h-11 flex-wrap items-center gap-3 rounded-md border px-3 py-1.5">
                <label className="flex min-h-10 items-center gap-2">
                  <span className="text-foreground text-xs font-medium">
                    {t("adminDashboard.libraries.enableAllSearch", "Search")}
                  </span>
                  <Switch
                    aria-label={t("adminDashboard.libraries.enableAllSearch", "Search")}
                    checked={allSearchEnabledState === true}
                    disabled={isLoading || isUpdatingAll || !libraries || libraries.length === 0}
                    onCheckedChange={handleToggleAllSearch}
                  />
                </label>
                <div className="bg-border h-4 w-px" />
                <label className="flex min-h-10 items-center gap-2">
                  <span className="text-foreground text-xs font-medium">
                    {t("adminDashboard.libraries.enableAllIndexing", "Indexing")}
                  </span>
                  <Switch
                    aria-label={t("adminDashboard.libraries.enableAllIndexing", "Indexing")}
                    checked={allIndexingEnabledState === true}
                    disabled={isLoading || isUpdatingAll || !libraries || libraries.length === 0}
                    onCheckedChange={handleToggleAllIndexing}
                  />
                </label>
              </div>
              <DelegationModeControl capability="libraries" />
            </div>
          </>
        }
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 pb-24 sm:pb-0">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
              <div className="relative min-w-0 flex-1">
                <Search
                  aria-hidden="true"
                  className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2"
                />
                <Input
                  aria-label="Search libraries"
                  className="min-h-11 pl-9"
                  placeholder="Search libraries"
                  value={librarySearch}
                  onChange={(event) => setLibrarySearch(event.target.value)}
                />
              </div>
              <Select
                value={libraryStatus}
                onValueChange={(value) => setLibraryStatus(value as "all" | "active" | "hidden")}
              >
                <SelectTrigger
                  aria-label="Library status"
                  className="min-h-11 w-full shrink-0 sm:w-40"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-muted/30 grid grid-cols-3 gap-2 rounded-lg border p-3 text-sm">
              <div>
                <span className="text-foreground block text-xs">Libraries</span>
                <span className="font-semibold tabular-nums">{libraries?.length ?? 0}</span>
              </div>
              <div>
                <span className="text-foreground block text-xs">Searchable</span>
                <span className="font-semibold tabular-nums">{searchableCount}</span>
              </div>
              <div>
                <span className="text-foreground block text-xs">Indexing</span>
                <span className="font-semibold tabular-nums">{indexingCount}</span>
              </div>
            </div>

            <LibrariesTable
              canManageIndexing={canManageIndexing}
              disableAll={isUpdatingAll}
              isUpdating={updatingLibraryId}
              libraries={filteredLibraries}
              onBrowseDocuments={handleBrowseDocuments}
              onToggleIndexingEnabled={handleToggleIndexingEnabled}
              onToggleSearchEnabled={handleToggleSearchEnabled}
              onDelete={
                canManagePlatformOnly
                  ? (library) => {
                      setDeletingLibrary(library as Library)
                      setIsDeleteDialogOpen(true)
                    }
                  : undefined
              }
              onEdit={
                canManagePlatformOnly
                  ? (library) => {
                      setEditingLibrary(library as Library)
                      setIsEditLibraryDialogOpen(true)
                    }
                  : undefined
              }
              onEditAccess={(libraryId) => {
                setAccessLibraryId(libraryId)
                setIsAccessDialogOpen(true)
              }}
            />

            {canManagePlatformOnly ? (
              <div className="bg-background/95 sticky bottom-0 z-10 -mx-2 px-2 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:hidden">
                <Button
                  className="min-h-11 w-full shadow-lg"
                  disabled={isUpdatingAll}
                  type="button"
                  onClick={() => setIsAddLibraryDialogOpen(true)}
                >
                  <Plus className="size-4" />
                  {t("adminDashboard.libraries.addLibrary", "Add Library")}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </CollapsibleManagementSection>

      <AccessPolicyDialog
        isLoading={isLoadingBaseAccessPolicy}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={isBaseAccessPolicyError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.libraries.baseAccessDescription", {
          defaultValue:
            "Default access policy applied to all libraries unless overridden per library.",
        })}
        title={t("adminDashboard.libraries.baseAccessTitle", {
          defaultValue: "Base access policy — Libraries",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingAccessPolicy}
        isSaving={updateAccessMutation.isPending || clearAccessMutation.isPending}
        loadError={isAccessPolicyError || isBaseAccessPolicyError}
        open={isAccessDialogOpen}
        policy={libraryAccessPolicy}
        description={
          accessLibraryId
            ? t("adminDashboard.access.dialogDescription", {
                defaultValue: "Configure who can access this resource.",
              })
            : undefined
        }
        inherit={{
          enabled: true,
          inheritByDefault,
          policy: baseAccessPolicy,
          label: t("adminDashboard.libraries.inheritBaseAccess", {
            defaultValue: "Inherit access for all",
          }),
          description: t("adminDashboard.libraries.inheritBaseAccessDescription", {
            defaultValue:
              "When enabled, this library uses the section baseline access policy. Disable to override for this library.",
          }),
          onSaveInherited: async () => {
            if (!accessLibraryId) return
            await clearAccessMutation.mutateAsync({ libraryId: accessLibraryId })
            void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
          },
        }}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: selectedLibrary?.display_name ?? accessLibraryId ?? "",
        })}
        onOpenChange={(open) => {
          setIsAccessDialogOpen(open)
          if (!open) {
            setAccessLibraryId(undefined)
          }
        }}
        onSave={async (policy) => {
          if (!accessLibraryId) return
          try {
            await updateAccessMutation.mutateAsync({ libraryId: accessLibraryId, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      {/* Add Library Dialog */}
      <AddLibraryDialog
        existingLibraries={libraries ?? []}
        isOpen={isAddLibraryDialogOpen}
        isPending={createLibraryMutation.isPending}
        embeddingModels={
          embeddingModels
            ?.filter((m) => m.enabled)
            .map((m) => ({
              id: m.id,
              displayName: m.displayName || m.id,
            })) ?? []
        }
        onOpenChange={setIsAddLibraryDialogOpen}
        onSubmit={handleAddLibrary}
      />

      {/* Edit Library Dialog */}
      <EditLibraryDialog
        existingLibraries={libraries ?? []}
        isOpen={isEditLibraryDialogOpen}
        isPending={updateLibraryConfigMutation.isPending}
        library={editingLibrary}
        embeddingModels={
          embeddingModels?.map((m) => ({ id: m.id, displayName: m.displayName || m.id })) ?? []
        }
        onSubmit={handleEditLibrary}
        onOpenChange={(open) => {
          setIsEditLibraryDialogOpen(open)
          if (!open) setEditingLibrary(null)
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteLibraryDialog
        isOpen={isDeleteDialogOpen}
        isPending={deleteLibraryMutation.isPending}
        library={deletingLibrary}
        onConfirm={handleDeleteLibrary}
        onOpenChange={setIsDeleteDialogOpen}
      />

      {/* Document Browser Dialog */}
      <LibraryDocumentBrowser
        isOpen={isDocumentBrowserOpen}
        libraryId={browsingLibrary?.id ?? null}
        libraryName={browsingLibrary?.name ?? null}
        onOpenChange={(open: boolean) => {
          setIsDocumentBrowserOpen(open)
          if (!open) setBrowsingLibrary(null)
        }}
        onRetryFile={
          canManagePlatformOnly
            ? (libraryId: string, filename: string) => handleRetryFailed(libraryId, [filename])
            : undefined
        }
      />
    </>
  )
}
